import 'react';
import React, { useState, useContext, createContext, useEffect } from 'react'
import { AccountDTO } from './models/Identity';
import accountsRequest from './api/accountsRequest';
import { sessionId } from './localStorage';
import { useAuth } from 'react-oidc-context';
import configurationRequest from './api/configurationRequest';
import { PayrollOffice } from './models/Enums';
import { emitter, EVENT_CONSTANTS } from './events/emitt';
import { PayrollConfigurationDTO } from './models/Configuration';
import { signalRService } from './api/signalRService';
import { apiNotification } from './api/constants';
import { connect } from 'http2';

interface globalStateWithUpdateData {
  sidebarShow: boolean
  sidebarUnfoldable: boolean
  asideShow: boolean
  theme: string
  isAuthenticated: boolean,
  token: string | null,
  roles: string[],
  isPayrollOffice: boolean,
  payrollOffice: PayrollConfigurationDTO | null, // Updated type to match the DTO
  notificationsUnread: number,
  users: AccountDTO[]
  // SignalR properties
  signalRConnected: boolean,
  signalRConnecting: boolean,

  setSidebarShow: React.Dispatch<React.SetStateAction<boolean>>
  setSidebarUnfold: React.Dispatch<React.SetStateAction<boolean>>
  setAsideShow: React.Dispatch<React.SetStateAction<boolean>>
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  setToken: React.Dispatch<React.SetStateAction<string | null>>
  setRoles: React.Dispatch<React.SetStateAction<string[]>>
  setIsPayrollOffice: React.Dispatch<React.SetStateAction<boolean>>
  setNotificationUnread: React.Dispatch<React.SetStateAction<number>>
  setUsers: React.Dispatch<React.SetStateAction<AccountDTO[]>>
  // SignalR methods
  connectSignalR: () => Promise<void>
  disconnectSignalR: () => Promise<void>
}


const initialState: globalStateWithUpdateData = {
  sidebarShow: true,
  sidebarUnfoldable: false,
  asideShow: false,
  theme: 'default',
  isAuthenticated: false,
  payrollOffice: null, // Initialize as null
  token: null,
  roles: [],
  isPayrollOffice: false,
  notificationsUnread: 0,
  users: [],
  // SignalR initial state
  signalRConnected: false,
  signalRConnecting: false,

  setSidebarShow: () => { },
  setSidebarUnfold: () => { },
  setAsideShow: () => { },
  setIsAuthenticated: () => { },
  setToken: () => { },
  setRoles: () => { },
  setIsPayrollOffice: () => { },
  setNotificationUnread: () => { },
  setUsers: () => { },
  // SignalR methods
  connectSignalR: async () => { },
  disconnectSignalR: async () => { },
};


interface IGlobalContextProps {
  children: React.ReactNode
}

const GlobalContext = createContext<globalStateWithUpdateData>(initialState)
export const useGlobalContext = () => useContext(GlobalContext)

export function GlobalContextWrapper(p: IGlobalContextProps) {
  const auth = useAuth();

  const [sidebarShow, setSidebarShow] = useState<boolean>(true)
  const [sidebarUnfoldable, setSidebarUnfold] = useState<boolean>(true)
  const [asideShow, setAsideShow] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [token, setToken] = useState<string | null>(null)
  const [roles, setRoles] = useState<string[]>([])
  const [isPayrollOffice, setIsPayrollOffice] = useState<boolean>(false);
  const [payrollOffice, setPayrollOffice] = useState<PayrollConfigurationDTO | null>(null)
  const [notificationsUnread, setNotificationUnread] = useState<number>(0)
  const [users, setUsers] = useState<AccountDTO[]>([])
  // SignalR state
  const [signalRConnected, setSignalRConnected] = useState<boolean>(false)
  const [signalRConnecting, setSignalRConnecting] = useState<boolean>(false)


  useEffect(() => {
    if (localStorage) {
      let token = localStorage.getItem(sessionId.token);
      let roles = localStorage.getItem(sessionId.roles);

      if (roles !== null && roles !== "") {
        let rolesArray = JSON.parse(roles);
        setRoles(rolesArray)
        console.log('OAuthWrapper: load roles to local storage')
      }

      if (token != null) {
        setToken(token)
        setIsAuthenticated(true)
        console.log('OAuthWrapper: load token to local storage')
      }
    }

  }, [])

  const fetchPayrollOffice = () => {
    configurationRequest(auth.user?.access_token).GetPayrollOffice()
      .then(r => {
        if (r.PayrollOffice != PayrollOffice.None) {
          setPayrollOffice(r)
          setIsPayrollOffice(true)
        }
        else {
          setPayrollOffice(null)
          setIsPayrollOffice(false)
        }
      });
  }

  // fetch all users from the tenant when the token is present
  useEffect(() => {
    console.log('trigger update token')
    if (auth != null &&
      auth.user != null &&
      auth.user?.access_token != null &&
      auth.user?.access_token != '') {

      accountsRequest(auth.user?.access_token)
        .GetUsers()
        .then(r => {
          setUsers(r)
        })


      fetchPayrollOffice();

      accountsRequest(auth.user?.access_token)
        .GetMe()
        .then(r => {
          setRoles(r.Roles)
          setToken(auth.user?.access_token ?? "")
          setIsAuthenticated(auth.user?.access_token != '' ? true : false)
        })

      // connect SignalR if not connected
      connectSignalR();
    }
    return () => {
      // Cleanup function to disconnect SignalR if needed
      if (signalRConnected) {
        disconnectSignalR();
      }
    }
  }, [auth?.user?.access_token])

  // Event bus listeners
  useEffect(() => {
    // Register event listeners
    const handlePayrollConfigurationUpdate = (payload: any) => {
      console.log('Payroll configuration updated:', payload);
      // Refetch payroll office configuration
      fetchPayrollOffice();
    };

    // Subscribe to events
    emitter.on(EVENT_CONSTANTS.PAYROLL_CONFIGURATION_UPDATE, handlePayrollConfigurationUpdate);

    // Cleanup function to remove listeners
    return () => {
      emitter.off(EVENT_CONSTANTS.PAYROLL_CONFIGURATION_UPDATE, handlePayrollConfigurationUpdate);
    };
  }, [auth?.user?.access_token]); // Re-subscribe when auth changes

  // SignalR connection methods
  const connectSignalR = async (): Promise<void> => {
    if (!auth?.user?.access_token || signalRConnecting || signalRConnected) {
      console.log('SignalR: Connection conditions not met', {
        hasToken: !!auth?.user?.access_token,
        isConnecting: signalRConnecting,
        isConnected: signalRConnected
      });
      return;
    }

    try {
      setSignalRConnecting(true);
      
      // Test the connection first
      console.log('SignalR: Testing connection to:', apiNotification.hubAdminUrl);
      const canConnect = await signalRService.testConnection(apiNotification.hubAdminUrl, auth.user.access_token);
      
      if (!canConnect) {
        throw new Error('SignalR endpoint test failed. Check server configuration and URL.');
      }
      
      console.log('SignalR: Connection test passed, proceeding with connection...');
      await signalRService.connect(apiNotification.hubAdminUrl, auth.user.access_token);
      setSignalRConnected(true);
      console.log('SignalR: Successfully connected');
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      setSignalRConnected(false);
    } finally {
      setSignalRConnecting(false);
    }
  };

  const disconnectSignalR = async (): Promise<void> => {
    try {
      await signalRService.disconnect();
      setSignalRConnected(false);
    } catch (error) {
      console.error('Failed to disconnect from SignalR:', error);
    }
  };

  // Auto-connect SignalR when authenticated
  useEffect(() => {
    if (isAuthenticated && token && !signalRConnected && !signalRConnecting) {
      connectSignalR();
    } else if (!isAuthenticated && signalRConnected) {
      disconnectSignalR();
    }
  }, [isAuthenticated, token, signalRConnected, signalRConnecting]);


  const gState: globalStateWithUpdateData = {
    sidebarShow,
    sidebarUnfoldable,
    asideShow,
    theme: 'default',
    isAuthenticated,
    token,
    roles,
    isPayrollOffice,
    payrollOffice,
    notificationsUnread,
    users,
    // SignalR state
    signalRConnected,
    signalRConnecting,

    setSidebarShow,
    setSidebarUnfold,
    setAsideShow,
    setIsAuthenticated,
    setToken,
    setRoles,
    setIsPayrollOffice,
    setNotificationUnread,
    setUsers,
    // SignalR methods
    connectSignalR,
    disconnectSignalR,
  }

  return (
    <GlobalContext.Provider value={gState}>{p.children}</GlobalContext.Provider>

  )
}